import {
    Colors,
    ConfirmDialog,
    Icon,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { useState } from 'react';

import type { CompetencyProfile } from '@/types/competency/CompetencyProfile';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { MUTED } from '@/pages/CompetencyProfilesAdmin/helpers';
import { getTextByLanguage } from '@/utils/localization';

type ProfileGridProps = {
    profiles: CompetencyProfile[];
    onSelectProfile: (profileId: string) => void;
    /** Absent, the grid offers no "Create new profile" tile. */
    onCreateGuided?: () => void;
    /** When false, the "Create new profile" button is disabled with an explanatory tooltip. */
    canManageCompetencies: boolean;
    getProfileCounts: (profile: CompetencyProfile) => {
        activeCount: number;
        priorityCount: number;
    };
};

export const ProfileGrid = ({
    profiles,
    onSelectProfile,
    onCreateGuided,
    canManageCompetencies,
    getProfileCounts,
}: ProfileGridProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const [showNoPermissionModal, setShowNoPermissionModal] = useState(false);

    return (
        <div className={'flex flex-col gap-4'}>
            <p className={'text-navy text-[18px] font-semibold'}>
                {t('competencies.competencyProfilesAdmin.selector.profilesHeading')}
            </p>

            {profiles.length === 0 ? (
                <p
                    className={'py-12 text-center text-base'}
                    style={{ color: MUTED }}
                >
                    {t('competencies.competencyProfilesAdmin.selector.profilesEmpty')}
                </p>
            ) : (
                <div className={'responsive-grid-1-2-3 gap-6'}>
                    {onCreateGuided && (
                        <TooltipProvider>
                            <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                    <span className={'block h-full'}>
                                        <button
                                            type={'button'}
                                            onClick={
                                                canManageCompetencies
                                                    ? onCreateGuided
                                                    : () => setShowNoPermissionModal(true)
                                            }
                                            className={[
                                                'border-primary/50 relative flex h-full min-h-[180px] w-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed bg-white/80 p-5 text-center backdrop-blur-sm transition-all sm:p-[32px]',
                                                canManageCompetencies
                                                    ? 'hover:border-primary hover:bg-primary/5 hover:shadow-lg'
                                                    : 'cursor-pointer opacity-60',
                                            ].join(' ')}
                                            data-testid={'roster-selector-create-new'}
                                        >
                                            {!canManageCompetencies && (
                                                <span
                                                    className={
                                                        'absolute top-3 right-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-200 text-gray-500'
                                                    }
                                                >
                                                    <Icon
                                                        name={'security-lock'}
                                                        size={'md'}
                                                        color={'inherit'}
                                                    />
                                                </span>
                                            )}

                                            <span
                                                className={
                                                    'bg-primary/10 flex h-13 w-13 items-center justify-center rounded-full'
                                                }
                                            >
                                                <Icon
                                                    name={'essential-add'}
                                                    color={Colors.PRIMARY}
                                                    size={'lg'}
                                                />
                                            </span>

                                            <span className={'text-navy text-[18px] font-semibold'}>
                                                {t(
                                                    'competencies.competencyProfilesAdmin.selector.createNewTitle',
                                                )}
                                            </span>

                                            <span className={'text-[12px] text-gray-600'}>
                                                {t(
                                                    'competencies.competencyProfilesAdmin.selector.createNewDescription',
                                                )}
                                            </span>
                                        </button>
                                    </span>
                                </TooltipTrigger>

                                {!canManageCompetencies && (
                                    <TooltipContent
                                        side={'top'}
                                        className={'max-w-[280px]'}
                                    >
                                        <p>
                                            {t(
                                                'competencies.competencyProfilesAdmin.noCreatePermissionTooltip',
                                            )}
                                        </p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    )}

                    {profiles.map((profile) => {
                        const counts = getProfileCounts(profile);

                        return (
                            <button
                                key={profile.id}
                                type={'button'}
                                onClick={() => onSelectProfile(profile.id)}
                                className={
                                    'hover:border-primary/50 flex flex-col justify-between gap-2 rounded-xl border-2 border-white bg-white/80 p-[20px] text-left backdrop-blur-sm transition-all hover:shadow-lg sm:p-[32px]'
                                }
                                data-testid={`roster-selector-profile-${profile.id}`}
                            >
                                <div>
                                    <p className={'text-navy text-[18px] font-semibold'}>
                                        {getTextByLanguage(profile.name, language)}
                                    </p>

                                    <p className={'text-navy/70 my-2 text-[12px]'}>
                                        {getTextByLanguage(profile.description, language)}
                                    </p>
                                </div>

                                <div className={'flex flex-col items-center gap-2 sm:flex-row'}>
                                    <span
                                        className={
                                            'text-purple rounded-full bg-purple-100 px-3 py-[2px] text-[12px]'
                                        }
                                    >
                                        {t(
                                            'competencies.competencyProfilesAdmin.selector.skillCount',
                                            { count: counts.activeCount },
                                        )}
                                    </span>

                                    <span
                                        className={
                                            'bg-primary/10 rounded-full px-3 py-[2px] text-[12px] text-teal-500'
                                        }
                                    >
                                        {t(
                                            'competencies.competencyProfilesAdmin.selector.priorityCount',
                                            { count: counts.priorityCount },
                                        )}
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            <ConfirmDialog
                isOpen={showNoPermissionModal}
                title={t('competencies.competencyProfilesAdmin.noCreatePermissionTitle')}
                message={t('competencies.competencyProfilesAdmin.noCreatePermissionTooltip')}
                confirmLabel={t('common.close')}
                onConfirm={() => setShowNoPermissionModal(false)}
                onCancel={() => setShowNoPermissionModal(false)}
                hideCancel
                dataTestId={'grid-no-permission-modal'}
            />
        </div>
    );
};
